import { Controller, useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link as LinkIcon } from '@mui/icons-material';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CopyButton from 'shared/components/buttons/copy-button/copy-button';
import PopUp from 'shared/components/pop-up/pop-up';

import { useGatewaysContextProvider } from 'features/gateway/context/gateway-provider';
import Gateway from 'features/gateway/domain/models/gateway';
import { GatewayType } from 'features/gateway/domain/models/gateway-type';
import useGatewaySettingsIoTEdgeClusterClient from 'features/gateway/hooks/gateway-settings-iot-edge-cluster-client-hook';
import useGatewayValidation from 'features/gateway/hooks/gateway-validation-hook';
import styles from './gateway-settings-client.module.scss';

const GatewaySettingsIoTEdgeClusterDevice = (childGateway: Gateway | undefined, index: number, isSecondary: boolean = false) => {
  const { t } = useTranslation('gateway');
  const { gatewayDetailsHook } = useGatewaysContextProvider();
  const { validateChildGatewayName } = useGatewayValidation();

  const {
    deviceId,
    ioTEdgeConnectionString,
    generateIoTEdgeConnectionStringIsLoading,
    isGenerateConfirmationOpen,
    defaultIoTEdgeConnectionString,
    generateIoTEdgeClusterDeviceConnectionString: generateIoTEdgeConnectionString,
    submitGenerateIoTEdgeClusterDeviceConnectionString: submitGenerateIoTEdgeConnectionString,
    closeGenerateConfirmationPopup,
  } = useGatewaySettingsIoTEdgeClusterClient(childGateway);

  const { control } = useFormContext();

  const isEditing = gatewayDetailsHook.viewingMode === 'editing' || gatewayDetailsHook.viewingMode === 'creation';
  const isViewing = gatewayDetailsHook.viewingMode === 'viewing';

  const shouldShowSecondaryGateway = !isSecondary || (childGateway?.name && childGateway.name.trim() !== '');

  if (isViewing && isSecondary && !shouldShowSecondaryGateway) {
    return null;
  }

  return (
    <div className={styles.clusterItem}>
      {isEditing ? (
        <Controller
          name={`childGateways[${index}].name`}
          control={control}
          rules={{
            required: !isSecondary ? t('details.information.requiredHelperText') : false,
            validate: () => validateChildGatewayName(index),
          }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              slotProps={{
                htmlInput: {
                  'data-testid': 'nameInput',
                  maxLength: 255,
                },
              }}
              id={`nameInput-${index}`}
              label={t('details.information.nameLabel')}
              variant="outlined"
              error={!!error}
              helperText={error?.message?.toString()}
              className={styles.textfield}
            />
          )}
        />
      ) : (
        <Typography variant="subtitle1">{childGateway?.name ?? ''}</Typography>
      )}
      <div className={styles.container}>
        <div className={styles.infoItem}>
          <Typography variant="subtitle1">{t('details.settings.ioTEdgeDeviceIdLabel')}:</Typography>
          <Typography variant="subtitle1">
            {gatewayDetailsHook.currentSelectedGateway?.type === GatewayType.IoTEdgeMediaServerGatewayCluster
              ? t('details.settings.centralSupportGatewayConnectionStringLabel')
              : t('details.settings.ioTEdgeConnectionStringLabel')}
            :
          </Typography>
        </div>
        <div className={styles.infoItem}>
          <div className={styles.secret}>
            <Typography variant="subtitle1">{deviceId ?? '-'}</Typography>
            {deviceId !== undefined && <CopyButton contentToCopy={deviceId} />}
          </div>
          <div className={styles.secret}>
            <Typography variant="subtitle1">{ioTEdgeConnectionString}</Typography>
            {ioTEdgeConnectionString !== defaultIoTEdgeConnectionString && <CopyButton contentToCopy={ioTEdgeConnectionString} />}
          </div>
        </div>
      </div>
      {isViewing &&
        shouldShowSecondaryGateway &&
        (ioTEdgeConnectionString === defaultIoTEdgeConnectionString ? (
          <Button
            variant="outlined"
            onClick={generateIoTEdgeConnectionString}
            startIcon={<LinkIcon />}
            loading={generateIoTEdgeConnectionStringIsLoading}
            loadingPosition="start"
            data-testid="generate-secret-button"
          >
            {t('details.settings.clientSecret.generateButton')}
          </Button>
        ) : (
          <Typography variant="subtitle1">{t('details.settings.clientSecret.warningIoTEdge')}</Typography>
        ))}
      <PopUp
        isOpen={isGenerateConfirmationOpen}
        title={t('details.settings.clientSecret.confirmation.title')}
        body={<Trans t={t} i18nKey="details.settings.clientSecret.confirmation.bodyText" />}
        primaryButtonText={t('details.settings.clientSecret.confirmation.confirmButton')}
        secondaryButtonText={t('details.settings.clientSecret.confirmation.cancelButton')}
        handleOnClose={closeGenerateConfirmationPopup}
        secondaryButtonAction={closeGenerateConfirmationPopup}
        primaryButtonAction={submitGenerateIoTEdgeConnectionString}
      />
    </div>
  );
};

const GatewaySettingsIoTEdgeClusterClient = () => {
  const { gatewayDetailsHook } = useGatewaysContextProvider();

  const activeGateway = gatewayDetailsHook.currentSelectedGateway?.childGateways?.find((x) => x.isActiveInCluster);
  const passiveGateway = gatewayDetailsHook.currentSelectedGateway?.childGateways?.find((x) => !x.isActiveInCluster);

  return (
    <>
      {GatewaySettingsIoTEdgeClusterDevice(
        activeGateway,
        activeGateway ? (gatewayDetailsHook.currentSelectedGateway?.childGateways?.indexOf(activeGateway) ?? 0) : 0,
        false,
      )}
      {GatewaySettingsIoTEdgeClusterDevice(
        passiveGateway,
        passiveGateway ? (gatewayDetailsHook.currentSelectedGateway?.childGateways?.indexOf(passiveGateway) ?? 1) : 1,
        true,
      )}
    </>
  );
};

export default GatewaySettingsIoTEdgeClusterClient;
